import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, Car, RefreshCw, Navigation, Clock, Phone, 
  User, Maximize2, Minimize2, Circle, Wifi, WifiOff,
  ChevronRight, Activity
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../services/api';

const STATUS_COLORS = {
  online: '#22c55e',    // green
  on_ride: '#8b5cf6',   // purple
  offline: '#6b7280',   // gray
  available: '#D4AF37', // gold
  busy: '#f59e0b'       // amber
};

const DriverLiveMap = ({ onSelectDriver }) => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const mapRef = useRef(null);
  const refreshInterval = useRef(null);

  const fetchDriverLocations = useCallback(async () => {
    try {
      // Fetch active drivers with their locations
      const response = await api.get('/workers?role=driver&status=active');
      const workersData = Array.isArray(response.data) ? response.data : (response.data?.workers || []);
      
      // Also fetch clock status to see who's online
      const clockResponse = await api.get('/clock/all-status').catch(() => ({ data: [] }));
      const clockData = Array.isArray(clockResponse.data) ? clockResponse.data : [];
      
      // Merge data
      const driversWithLocation = workersData.map(worker => {
        const clockStatus = clockData.find(c => c.worker_id === worker.id || c.user_id === worker.id);
        return {
          ...worker,
          is_online: clockStatus?.is_clocked_in || false,
          last_location: clockStatus?.last_location || worker.last_location,
          clock_in_time: clockStatus?.clock_in_time,
          current_ride: clockStatus?.current_ride_id
        };
      });
      
      setDrivers(driversWithLocation);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch driver locations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDriverLocations();
    // Refresh every 30 seconds
    refreshInterval.current = setInterval(fetchDriverLocations, 30000);
    return () => {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
    };
  }, [fetchDriverLocations]);

  const handleRefresh = () => {
    setLoading(true);
    fetchDriverLocations();
  };

  const getDriverStatus = (driver) => {
    if (!driver.is_online) return 'offline';
    if (driver.current_ride) return 'on_ride';
    return 'available';
  };

  const getStatusLabel = (status) => {
    const labels = {
      online: 'Online',
      on_ride: 'On Ride',
      offline: 'Offline',
      available: 'Available',
      busy: 'Busy'
    };
    return labels[status] || 'Unknown';
  };

  const onlineDrivers = drivers.filter(d => d.is_online);
  const offlineDrivers = drivers.filter(d => !d.is_online);

  // Simple map placeholder (could be replaced with real map integration)
  const MapPlaceholder = () => (
    <div className="relative bg-[#1a1a1a] rounded-xl overflow-hidden" style={{ height: expanded ? '500px' : '300px' }}>
      {/* Map background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] to-[#1a1a1a]">
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M54.627 0l.83.828-1.415 1.415L51.8 0h2.827zM5.373 0l-.83.828L5.96 2.243 8.2 0H5.374zM48.97 0l3.657 3.657-1.414 1.414L46.143 0h2.828zM11.03 0L7.372 3.657 8.787 5.07 13.857 0H11.03zm32.284 0L49.8 6.485 48.384 7.9l-7.9-7.9h2.83zM16.686 0L10.2 6.485 11.616 7.9l7.9-7.9h-2.83zM22.344 0L13.858 8.485 15.272 9.9l9.9-9.9h-2.828zM32 0l-3.657 3.657 1.414 1.414L searching33.415 1.414 34.827 0H32zM0 5.373l.828-.83L2.243 5.96 0 8.2V5.374zm0 5.656l.828-.829 5.657 5.657-1.414 1.414L0 12.343v-1.314zm0 5.656l.828-.828 11.313 11.314-1.414 1.414L0 18v-.314zm0 5.657l.828-.828L17.8 28.5l-1.414 1.414L0 13.543v10.142zM0 32l.828-.828L23.172 53.515l-1.414 1.414L0 33.086V32zm0 5.657l.828-.828L30 43.656l-1.414 1.414L0 16.542v21.114zm0 5.656l.828-.828L35.313 48.8l-1.414 1.414L0 21.87v21.443zM0 54.627l.828-.828L41.313 54.8l-1.414 1.414L0 16.385V54.627zM54.627 60L53.8 59.172l1.414-1.414L60 62.827v-2.827l-5.373.627zM60 48.97l-3.657 3.657 1.414 1.414L60 51.8v-2.83zm-6.485-6.485L60 48.97v-2.828l-4.243-4.243-1.414 1.414-1.828 1.172zm6.485-6.485l-3.657 3.657 1.414 1.414L60 38.313v-2.828l.627.515zm-6.485-6.485l6.485 6.485V32l-5.657-5.657-1.414 1.414.586.757zm6.485-6.485l-3.657 3.657 1.414 1.414L60 24.657v-2.827l.627.515zM60 16.686l-6.485 6.485 1.414 1.414L60 18.515v-1.829l.627-.515zM60 11.03l-9.9 9.9 1.414 1.414L60 13.857v-2.828l.627.515zM60 5.373l-15.557 15.556 1.414 1.414L60 8.2V5.374l.627-.515zM38.343 0L60 21.656V18l-20.243-20.243L38.343 0zM27.03 0L60 32.97v-2.828L29.858 0H27.03zM16.686 0L60 43.313v-2.828L19.514 0H16.686zM6.343 0L60 53.656v-2.828L9.172 0H6.343z' fill='%23D4AF37' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          }}
        />
      </div>
      
      {/* Driver markers */}
      <div className="relative w-full h-full">
        {onlineDrivers.map((driver, idx) => {
          const status = getDriverStatus(driver);
          // Position drivers in a grid pattern for visualization
          const row = Math.floor(idx / 3);
          const col = idx % 3;
          const left = 15 + col * 30 + (Math.random() * 10);
          const top = 20 + row * 25 + (Math.random() * 10);
          
          return (
            <motion.div
              key={driver.id}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute cursor-pointer group"
              style={{ left: `${left}%`, top: `${top}%` }}
              onClick={() => setSelectedDriver(selectedDriver?.id === driver.id ? null : driver)}
            >
              {/* Pulse effect for online drivers */}
              {driver.is_online && (
                <div 
                  className="absolute inset-0 rounded-full animate-ping opacity-30"
                  style={{ backgroundColor: STATUS_COLORS[status] }}
                />
              )}
              
              {/* Driver marker */}
              <div 
                className={`relative w-10 h-10 rounded-full flex items-center justify-center border-2 shadow-lg transition-transform group-hover:scale-110 ${
                  selectedDriver?.id === driver.id ? 'scale-125' : ''
                }`}
                style={{ 
                  backgroundColor: '#111',
                  borderColor: STATUS_COLORS[status]
                }}
              >
                <Car className="w-5 h-5" style={{ color: STATUS_COLORS[status] }} />
              </div>
              
              {/* Driver name tooltip */}
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="bg-black/80 text-white text-xs px-2 py-1 rounded">
                  {driver.full_name?.split(' ')[0]}
                </span>
              </div>
            </motion.div>
          );
        })}
        
        {/* Empty state */}
        {onlineDrivers.length === 0 && !loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <WifiOff className="w-12 h-12 text-white/20 mx-auto mb-2" />
              <p className="text-white/50">No drivers online</p>
            </div>
          </div>
        )}
        
        {/* Loading */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin" />
          </div>
        )}
      </div>
      
      {/* Expand button */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="absolute top-3 right-3 p-2 bg-black/50 rounded-lg hover:bg-black/70 transition-colors"
      >
        {expanded ? <Minimize2 className="w-4 h-4 text-white" /> : <Maximize2 className="w-4 h-4 text-white" />}
      </button>
      
      {/* Legend */}
      <div className="absolute bottom-3 left-3 flex items-center gap-3 bg-black/70 px-3 py-2 rounded-lg">
        <div className="flex items-center gap-1">
          <Circle className="w-3 h-3 fill-current" style={{ color: STATUS_COLORS.available }} />
          <span className="text-xs text-white/70">Available</span>
        </div>
        <div className="flex items-center gap-1">
          <Circle className="w-3 h-3 fill-current" style={{ color: STATUS_COLORS.on_ride }} />
          <span className="text-xs text-white/70">On Ride</span>
        </div>
        <div className="flex items-center gap-1">
          <Circle className="w-3 h-3 fill-current" style={{ color: STATUS_COLORS.offline }} />
          <span className="text-xs text-white/70">Offline</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="card overflow-hidden" data-testid="driver-live-map">
      {/* Header */}
      <div className="p-4 border-b border-[var(--border)] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Navigation className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold">Driver Live Map</h3>
            <p className="text-xs text-[var(--text-muted)]">
              {onlineDrivers.length} online • Last updated {lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Map */}
      <MapPlaceholder />

      {/* Selected Driver Panel */}
      <AnimatePresence>
        {selectedDriver && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-[var(--border)] overflow-hidden"
          >
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4C2C2] flex items-center justify-center text-black font-bold">
                    {selectedDriver.full_name?.charAt(0) || 'D'}
                  </div>
                  <div>
                    <h4 className="font-semibold">{selectedDriver.full_name}</h4>
                    <div className="flex items-center gap-2 text-sm">
                      <span 
                        className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                        style={{ 
                          backgroundColor: `${STATUS_COLORS[getDriverStatus(selectedDriver)]}20`,
                          color: STATUS_COLORS[getDriverStatus(selectedDriver)]
                        }}
                      >
                        <Circle className="w-2 h-2 fill-current" />
                        {getStatusLabel(getDriverStatus(selectedDriver))}
                      </span>
                      {selectedDriver.rating && (
                        <span className="text-[var(--text-muted)]">★ {selectedDriver.rating.toFixed(1)}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {selectedDriver.phone && (
                    <a href={`tel:${selectedDriver.phone}`}>
                      <Button size="sm" variant="outline" className="border-green-500 text-green-500">
                        <Phone className="w-4 h-4" />
                      </Button>
                    </a>
                  )}
                  {onSelectDriver && (
                    <Button 
                      size="sm" 
                      className="bg-[#D4AF37] text-black"
                      onClick={() => onSelectDriver(selectedDriver)}
                    >
                      View Details
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Driver stats */}
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="text-center p-3 bg-[var(--background-subtle)] rounded-lg">
                  <p className="text-lg font-bold text-[var(--primary)]">
                    {selectedDriver.completed_rides || 0}
                  </p>
                  <p className="text-xs text-[var(--text-muted)]">Rides</p>
                </div>
                <div className="text-center p-3 bg-[var(--background-subtle)] rounded-lg">
                  <p className="text-lg font-bold text-green-400">
                    {selectedDriver.acceptance_rate || 0}%
                  </p>
                  <p className="text-xs text-[var(--text-muted)]">Acceptance</p>
                </div>
                <div className="text-center p-3 bg-[var(--background-subtle)] rounded-lg">
                  <p className="text-lg font-bold text-purple-400">
                    {selectedDriver.clock_in_time 
                      ? `${Math.round((Date.now() - new Date(selectedDriver.clock_in_time).getTime()) / 60000)}m`
                      : '-'
                    }
                  </p>
                  <p className="text-xs text-[var(--text-muted)]">Online</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Driver List */}
      <div className="border-t border-[var(--border)]">
        <div className="p-3 flex items-center justify-between bg-[var(--background-subtle)]">
          <span className="text-sm font-medium">All Drivers ({drivers.length})</span>
          <span className="text-xs text-[var(--text-muted)]">
            <Wifi className="w-3 h-3 inline mr-1 text-green-400" />
            {onlineDrivers.length} online
          </span>
        </div>
        <div className="max-h-[200px] overflow-y-auto">
          {drivers.map(driver => {
            const status = getDriverStatus(driver);
            return (
              <div
                key={driver.id}
                className={`flex items-center justify-between p-3 hover:bg-[var(--background-subtle)] cursor-pointer transition-colors border-b border-[var(--border)] last:border-0 ${
                  selectedDriver?.id === driver.id ? 'bg-[var(--primary)]/10' : ''
                }`}
                onClick={() => setSelectedDriver(selectedDriver?.id === driver.id ? null : driver)}
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full bg-[var(--background-subtle)] flex items-center justify-center">
                      <User className="w-4 h-4 text-[var(--text-muted)]" />
                    </div>
                    <div 
                      className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[var(--background)]"
                      style={{ backgroundColor: STATUS_COLORS[status] }}
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{driver.full_name}</p>
                    <p className="text-xs text-[var(--text-muted)]">{getStatusLabel(status)}</p>
                  </div>
                </div>
                {driver.current_ride && (
                  <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full">
                    On Ride
                  </span>
                )}
              </div>
            );
          })}
          {drivers.length === 0 && (
            <div className="p-6 text-center text-[var(--text-muted)]">
              <Car className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No drivers registered yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverLiveMap;
